import axios from 'axios'
import { API_BASE_URL, API_ENDPOINTS } from '@/config/api'

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Setup axios interceptors
export const setupAxios = () => {
  // Request interceptor for JWT token
  api.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // Response interceptor for error handling
  api.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status
      const data = error?.response?.data
      const cfg = error?.response?.config || error?.config || {}
      const method = (cfg.method || '').toUpperCase()
      const url = cfg.url || cfg.baseURL ? `${cfg.baseURL || ''}${cfg.url || ''}` : cfg.url
      const suppress = cfg.headers && (cfg.headers['x-suppress-error-log'] || cfg.headers['X-Suppress-Error-Log'])
      if (!suppress) {
        console.error('API Error:', { status, method, url, data, message: error.message })
      }
      
      // Handle different error types
      if (status === 401) {
        // Unauthorized - clear auth data and redirect
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('orgId')
        window.location.href = '/login'
      } else if (status === 403) {
        // Forbidden - show permission error
        console.warn('Access denied: Insufficient permissions')
      } else if (status >= 500) {
        // Server errors - could trigger global error notification
        console.error('Server error: Please try again later')
      } else if (!navigator.onLine) {
        // Network connectivity issues
        console.error('Network error: Please check your connection')
      }
      
      throw error
    }
  )
}

// Base API service class
export class BaseApiService {
  constructor(baseEndpoint) {
    this.baseEndpoint = baseEndpoint
  }

  // Generic CRUD operations
  async getAll(params = {}) {
    const response = await api.get(this.baseEndpoint, { params })
    return response.data
  }

  async getById(id) {
    const response = await api.get(`${this.baseEndpoint}/${id}`)
    return response.data
  }

  async create(data) {
    const response = await api.post(this.baseEndpoint, data)
    return response.data
  }

  async update(id, data) {
    const response = await api.put(`${this.baseEndpoint}/${id}`, data)
    return response.data
  }

  async delete(id) {
    const response = await api.delete(`${this.baseEndpoint}/${id}`)
    return response.data
  }

  // Utility method for custom endpoints
  async customRequest(method, endpoint, data = null, config = {}) {
    const response = await api({
      method,
      url: endpoint,
      data,
      ...config
    })
    return response.data
  }
}

// Specific API services
export class AuthApiService extends BaseApiService {
  constructor() {
    super('/api/auth')
  }

  async login(credentials) {
    return this.customRequest('post', '/api/auth/login', credentials)
  }

  async register(userData) {
    return this.customRequest('post', '/api/auth/register', userData)
  }

  async me() {
    return this.customRequest('get', '/api/auth/me')
  }

  async createUserDirect(userData) {
    return this.customRequest('post', '/api/auth/create-user-direct', userData)
  }
}

export class LLMApiService extends BaseApiService {
  constructor() {
    super('/api/llms/models')  // Base endpoint for models
  }

  async getModels() {
    // Get all models from /api/llms/models
    return this.getAll()
  }

  async createModel(data) {
    // Create model at POST /api/llms/models
    return this.create(data)
  }

  async updateModel(id, data) {
    // Update model at PUT /api/llms/models/{id}
    return this.update(id, data)
  }

  async deleteModel(id) {
    // Delete model at DELETE /api/llms/models/{id}
    return this.delete(id)
  }

  async getModelById(id) {
    // Get specific model at GET /api/llms/models/{id}
    return this.getById(id)
  }

  async getModelOptions(provider) {
    return this.customRequest('get', '/api/llms/models/options', null, {
      params: { provider }
    })
  }

  async chat(data) {
    return this.customRequest('post', '/api/llms/chat', data)
  }
}

export class AgentsApiService extends BaseApiService {
  constructor() {
    super('/api/agents')
  }

  async getConversations(agentId, includeEmpty = false) {
    const endpoint = includeEmpty 
      ? `/api/agents/${agentId}/conversations/all`
      : `/api/agents/${agentId}/conversations`
    return this.customRequest('get', endpoint)
  }

  // Agent chat now goes through unified chat API
  async chatViaUnified(agentId, { message, session_id, stream = false, context = {} }) {
    const payload = { message, session_id, stream, context }
    // target=agent uses URL param id; agentId in body not required
    return this.customRequest('post', `/api/chat/agent/${agentId}`, payload)
  }

  async getSessions(agentId) {
    // Prefer unified chat sessions
    return this.customRequest('get', `/api/chat/agent/${agentId}/sessions`)
  }

  async getSession(sessionId) {
    // Fetch a single agent session by ID
    return this.customRequest('get', `/api/agents/sessions/${sessionId}`)
  }

  async deleteSession(sessionId) {
    // Delete a specific agent session by ID
    return this.customRequest('delete', `/api/agents/sessions/${sessionId}`)
  }
}

// Unified Chat API service
export class ChatApiService {
  async chat(target, id, { message, chat_mode = 'model', agent_id, model_id, session_id, stream = false, context = {}, history = [] }) {
    // Minimal payload with only required fields
    const payload = {
      message: message,
      chat_mode: chat_mode
    }

    // Only add optional fields if they have values
    if (model_id && chat_mode === 'model') {
      payload.model_id = model_id
    }
    if (agent_id && chat_mode === 'agent') {
      payload.agent_id = agent_id
    }
    if (session_id) {
      payload.session_id = session_id
    }

    return api.post(`/api/chat/${target}/${id}`, payload).then(r => r.data)
  }

  async chatTask(id, { message, session_id, stream = false, context = {}, model_id = null, agent_id = null }) {
    const payload = { message, session_id, stream, context }
    if (model_id) payload.model_id = model_id
    if (agent_id) payload.agent_id = agent_id
    return api.post(`/api/chat/task/${id}`, payload).then(r => r.data)
  }

  async getSessions(target, id, params = {}) {
    return api.get(`/api/chat/${target}/${id}/sessions`, { params }).then(r => r.data)
  }
}

// Workstreams API service
export class WorkstreamsApiService extends BaseApiService {
  constructor() {
    super('/api/workstreams')
  }
  async getMembers(id) { return this.customRequest('get', `/api/workstreams/${id}/members`) }
  async addMember(id, data) { 
    // Backend expects { member_id, role }
    return this.customRequest('post', `/api/workstreams/${id}/members`, data) 
  }
  async removeMember(id, memberID) { return this.customRequest('delete', `/api/workstreams/${id}/members/${memberID}`) }
  async updateMemberRole(id, memberID, data) {
    // Backend expects { role }
    return this.customRequest('put', `/api/workstreams/${id}/members/${memberID}/role`, data)
  }
  // Bootstrap or return default agent for this workstream
  async bootstrapAgent(id) { return this.customRequest('post', `/api/workstreams/${id}/agent/bootstrap`) }
  // Assign an existing agent to this workstream (PUT /api/workstreams/:id/agent)
  async assignAgent(id, agent_id) { return this.customRequest('put', `/api/workstreams/${id}/agent`, { agent_id }) }
  // Workstream chat (POST /api/workstreams/:id/chat)
  async chat(id, data) { return this.customRequest('post', `/api/workstreams/${id}/chat`, data) }

  // List only the current user's workstreams (best-effort, tries multiple shapes)
  async listMine(params = {}) {
    const silent = { headers: { 'x-suppress-error-log': '1' } }
    // Try query-param based scoping first
    try {
      const data = await this.customRequest('get', '/api/workstreams', null, { params: { ...params, member: 'me' }, ...silent })
      return Array.isArray(data) ? data : (data?.workstreams || data?.items || data?.data || [])
    } catch (_) {}
    try {
      const data = await this.customRequest('get', '/api/workstreams', null, { params: { ...params, mine: 1 }, ...silent })
      return Array.isArray(data) ? data : (data?.workstreams || data?.items || data?.data || [])
    } catch (_) {}
    // Try user-scoped endpoints
    try {
      const res = await api.get('/api/users/me/workstreams', silent)
      const data = res?.data
      return Array.isArray(data) ? data : (data?.workstreams || data?.items || data?.data || [])
    } catch (_) {}
    try {
      const raw = localStorage.getItem('user')
      const user = raw ? JSON.parse(raw) : null
      const uid = user?.id || user?._id || user?.user_id
      if (uid) {
        const res = await api.get(`/api/users/${uid}/workstreams`, silent)
        const data = res?.data
        return Array.isArray(data) ? data : (data?.workstreams || data?.items || data?.data || [])
      }
    } catch (_) {}
    return []
  }
}

// Tasks API service
export class TasksApiService extends BaseApiService {
  constructor() { super('/api/tasks') }
  async assign(id, assigned_to) { return this.customRequest('put', `/api/tasks/${id}/assign`, { assigned_to }) }
  // Allow passing additional completion data (actual_hours, quality, notes)
  async complete(id, data = {}) { return this.customRequest('put', `/api/tasks/${id}/complete`, data) }
  async listByWorkstream(workstreamId, params = {}) { return this.customRequest('get', `/api/workstreams/${workstreamId}/tasks`, null, { params }) }
  async addAgent(id, agent_id) { return this.customRequest('post', `/api/tasks/${id}/agents`, { agent_id }) }
  async removeAgent(id, agent_id) { return this.customRequest('delete', `/api/tasks/${id}/agents/${agent_id}`) }
  // New: bulk assignee management
  async addAssignees(id, userIds = []) { return this.customRequest('post', `/api/tasks/${id}/assignees`, { userIds }) }
  async removeAssignees(id, userIds = []) { return this.customRequest('delete', `/api/tasks/${id}/assignees`, { userIds }) }
  // New: list current user's tasks
  async listMine(params = {}) {
    const res = await api.get('/api/tasks/my', { params })
    const data = res?.data
    return Array.isArray(data) ? data : (data?.tasks || [])
  }

  // AI Task Generation - validate task title and get type suggestion
  async validateTask(title, description = '') {
    return this.customRequest('post', '/api/tasks/validate', { title, description })
  }

  // Get all available task types with descriptions
  async getTaskTypes() {
    return this.customRequest('get', '/api/tasks/types')
  }

  // Generate AI task suggestions for a goal
  async generateForGoal(goalId, options = {}) {
    const { goal_title, goal_description, model_id, max_tasks = 10, include_types = true, focus_areas = [], detail_level = 'standard' } = options
    return this.customRequest('post', `/api/goals/${goalId}/tasks/generate`, {
      goal_title,
      goal_description,
      model_id,
      max_tasks,
      include_types,
      focus_areas,
      detail_level
    })
  }

  // Apply/create tasks from AI-generated suggestions
  async applyGeneratedTasks(goalId, workstreamId, tasks) {
    return this.customRequest('post', `/api/goals/${goalId}/tasks/apply?workstream_id=${workstreamId}`, { tasks })
  }

  // Chat about task planning for a goal
  async chatGoal(goalId, message, options = {}) {
    const { goal_title, goal_description, model_id, history = [] } = options
    return this.customRequest('post', `/api/goals/${goalId}/tasks/chat`, {
      message,
      goal_title,
      goal_description,
      model_id,
      history
    })
  }
}

// Goals API service (Workstream-scoped embedded goals)
export class GoalsApiService extends BaseApiService {
  constructor() { super('/api/goals') }

  // Global Goals
  async progress(id) {
    return this.customRequest('get', `/api/goals/${id}/progress`)
  }

  // Workstream-scoped helpers
  async listByWorkstream(workstreamId) { 
    return this.customRequest('get', `/api/workstreams/${workstreamId}/goals`) 
  }
  
  async createForWorkstream(workstreamId, data) { 
    return this.customRequest('post', `/api/workstreams/${workstreamId}/goals`, data) 
  }

  async getInWorkstream(workstreamId, goalId) {
    // Use the direct workstream goal endpoint /api/workstreams/:id/:goalId
    return this.customRequest('get', `/api/workstreams/${workstreamId}/${goalId}`)
  }
  
  // Update and delete now require workstreamId
  async update(workstreamId, goalId, data) { 
    // Use the correct workstream goals endpoint
    return this.customRequest('put', `/api/workstreams/${workstreamId}/goals/${goalId}`, data)
  }
  
  async delete(workstreamId, goalId) { 
    // Use the correct workstream goals endpoint
    return this.customRequest('delete', `/api/workstreams/${workstreamId}/goals/${goalId}`)
  }
}
export class KnowledgeBasesApiService extends BaseApiService {
  constructor() {
    super('/api/knowledge-bases')
  }

  async getDocuments(kbId) {
    return this.customRequest('get', `/api/knowledge-bases/${kbId}/documents`)
  }

  async uploadDocument(kbId, formData) {
    return this.customRequest('post', `/api/knowledge-bases/${kbId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  async deleteDocument(kbId, docId) {
    return this.customRequest('delete', `/api/knowledge-bases/${kbId}/documents/${docId}`)
  }

  async archive(kbId) {
    return this.customRequest('post', `/api/knowledge-bases/${kbId}/archive`)
  }

  async searchKnowledgeBase(kbId, query, limit = 10) {
    return this.customRequest('post', `/api/knowledge-bases/${kbId}/search`, {
      query,
      limit
    })
  }
}

export class MessagingPlatformsApiService extends BaseApiService {
  constructor() {
    super('/api/messaging')
  }

  async getPlatforms() {
    return this.customRequest('get', '/api/messaging/platforms')
  }

  async initialize(data) {
    return this.customRequest('post', '/api/messaging/initialize', data)
  }

  async updateConfig(data) {
    return this.customRequest('put', '/api/messaging/update/config', data)
  }

  async deleteConfig(platform) {
    return this.customRequest('delete', `/api/messaging/${platform}/config`)
  }

  async deleteConfigByInstance(platform, instanceId) {
    const url = `/api/messaging/${platform}/${instanceId}/config`
    try {
      const result = await this.customRequest('delete', url)
      return result
    } catch (error) {
      console.error('DELETE config failed:', {
        url,
        status: error.response?.status,
        message: error.message
      })
      throw error
    }
  }

  async getStatus(params = {}) {
    return this.customRequest('get', '/api/messaging/status', null, { params })
  }

  async testConnection(data) {
    return this.customRequest('post', '/api/messaging/initialize/test', data)
  }
}

// New Slack-specific API service for webhook management
export class SlackWebhooksApiService extends BaseApiService {
  constructor() {
    super('/api/slack')
  }

  async addWebhook(data) {
    return this.customRequest('post', '/api/slack/webhooks', data)
  }

  async getWebhooks() {
    return this.customRequest('get', '/api/slack/webhooks')
  }

  async updateWebhook(instanceId, data) {
    return this.customRequest('put', `/api/slack/webhooks/${instanceId}`, data)
  }

  async testWebhook(data) {
    return this.customRequest('post', '/api/slack/webhooks/test', data)
  }

  async sendMessage(data) {
    return this.customRequest('post', '/api/slack/messages/send', data)
  }
}

export class OrganizationsApiService extends BaseApiService {
  constructor() {
    super('/api/orgs')
  }

  async getMembers(orgId) {
    return this.customRequest('get', `/api/orgs/${orgId}/members`)
  }

  async addMember(orgId, userData) {
    return this.customRequest('post', `/api/orgs/${orgId}/members`, userData)
  }

  async updateMemberRole(orgId, userId, roleData) {
    return this.customRequest('put', `/api/orgs/${orgId}/members/${userId}/role`, roleData)
  }

  async removeMember(orgId, userId) {
    return this.customRequest('delete', `/api/orgs/${orgId}/members/${userId}`)
  }
}

export class UsersApiService extends BaseApiService {
  constructor() {
    super('/api/users')
  }
  async search(query) {
    return this.customRequest('get', '/api/users/search', null, { params: { q: query } })
  }
}

export class RolesApiService extends BaseApiService {
  constructor() {
    super('/api/roles')
  }
}

// Export service instances
export const authApi = new AuthApiService()
export const llmApi = new LLMApiService()
export const agentsApi = new AgentsApiService()
export const chatApi = new ChatApiService()
export const workstreamsApi = new WorkstreamsApiService()
export const tasksApi = new TasksApiService()
export const goalsApi = new GoalsApiService()
export const knowledgeBasesApi = new KnowledgeBasesApiService()
export const messagingPlatformsApi = new MessagingPlatformsApiService()
export const slackWebhooksApi = new SlackWebhooksApiService()
export const organizationsApi = new OrganizationsApiService()
export const usersApi = new UsersApiService()
export const rolesApi = new RolesApiService()
